import React, { useMemo } from 'react';
import type { ThemeColors } from '@flowcore-water/ui';
import type { InstructorActivityEntry } from '../types';

interface ActivityChartProps {
  activity: InstructorActivityEntry[];
  t: ThemeColors;
}

const MS_PER_DAY = 86400000;
const MIN_WEEKS = 2;
const MAX_WEEKS = 52;

/** Attempt to parse a hex color into r,g,b. Falls back to 34,197,94 (green). */
const hexToRgb = (hex: string): string => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (m) return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
  return '34,197,94';
};

/**
 * Build a smooth cubic-bezier SVG path through a set of points.
 * Uses Catmull-Rom → cubic Bezier conversion for natural smoothing.
 */
const smoothPath = (points: [number, number][]): string => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0][0]},${points[0][1]}`;
  if (points.length === 2) return `M${points[0][0]},${points[0][1]}L${points[1][0]},${points[1][1]}`;

  const tension = 0.3;
  let d = `M${points[0][0]},${points[0][1]}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension;

    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${p2[0]},${p2[1]}`;
  }
  return d;
};

/** Compact smooth area chart — GitHub contribution-style with gradient fill */
export const ActivityChart: React.FC<ActivityChartProps> = ({ activity, t }) => {
  const { bins, maxCount } = useMemo(() => {
    if (activity.length === 0) return { bins: [], maxCount: 0 };

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    let minDate = now;
    for (const e of activity) {
      const d = e.completedAt instanceof Date ? e.completedAt : new Date(e.completedAt);
      if (d < minDate) minDate = d;
    }

    const daySpan = Math.ceil((now.getTime() - minDate.getTime()) / MS_PER_DAY);
    const rawWeeks = Math.ceil(daySpan / 7);
    const weeks = Math.max(MIN_WEEKS, Math.min(MAX_WEEKS, rawWeeks));

    const todayDay = now.getDay();
    const daysSinceMonday = todayDay === 0 ? 6 : todayDay - 1;
    const thisMonday = new Date(now);
    thisMonday.setHours(0, 0, 0, 0);
    thisMonday.setDate(thisMonday.getDate() - daysSinceMonday);

    const weekBins: { start: Date; end: Date; count: number; passed: number; failed: number }[] = [];
    for (let w = weeks - 1; w >= 0; w--) {
      const start = new Date(thisMonday);
      start.setDate(start.getDate() - w * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      weekBins.push({ start, end, count: 0, passed: 0, failed: 0 });
    }

    for (const entry of activity) {
      const d = entry.completedAt instanceof Date ? entry.completedAt : new Date(entry.completedAt);
      for (const bin of weekBins) {
        if (d >= bin.start && d < bin.end) {
          bin.count++;
          if (entry.passed) bin.passed++;
          else bin.failed++;
          break;
        }
      }
    }

    return { bins: weekBins, maxCount: Math.max(1, ...weekBins.map((b) => b.count)) };
  }, [activity]);

  if (bins.length === 0) return null;

  const chartH = 40;
  const labelH = 12;
  const padL = 2;
  const padR = 2;
  const totalH = chartH + labelH;
  const svgW = 500;
  const plotW = svgW - padL - padR;

  const points: [number, number][] = bins.map((bin, i) => {
    const x = padL + (i / Math.max(1, bins.length - 1)) * plotW;
    const y = chartH - (bin.count / maxCount) * (chartH - 4) - 2; // 2px top/bottom padding
    return [x, y];
  });

  const linePath = smoothPath(points);
  // Area path: line path + close along bottom
  const lastPt = points[points.length - 1];
  const firstPt = points[0];
  const areaPath = `${linePath}L${lastPt[0]},${chartH}L${firstPt[0]},${chartH}Z`;

  const successRgb = hexToRgb(t.success);

  const fmtWeek = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Month label positions
  const monthLabels: { x: number; label: string }[] = [];
  for (let i = 0; i < bins.length; i++) {
    const prevMonth = i > 0 ? bins[i - 1].start.getMonth() : -1;
    if (i === 0 || bins[i].start.getMonth() !== prevMonth) {
      const x = padL + (i / Math.max(1, bins.length - 1)) * plotW;
      monthLabels.push({ x, label: bins[i].start.toLocaleDateString('en-US', { month: 'short' }) });
    }
  }

  return (
    <div
      style={{ backgroundColor: t.cardBg, border: `1px solid ${t.border}` }}
      className="rounded-lg px-4 py-3 mb-3"
    >
      <div className="flex items-center gap-3 mb-1">
        <span style={{ color: t.textMuted }} className="text-xs font-medium">Activity</span>
        <span style={{ color: t.textMuted }} className="text-xs ml-auto">
          {fmtWeek(bins[0].start)} — {fmtWeek(bins[bins.length - 1].end)}
        </span>
      </div>

      <svg
        width="100%"
        viewBox={`0 0 ${svgW} ${totalH}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id="activityGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`rgb(${successRgb})`} stopOpacity="0.4" />
            <stop offset="100%" stopColor={`rgb(${successRgb})`} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gradient fill area */}
        <path d={areaPath} fill="url(#activityGrad)" />

        {/* Smooth line */}
        <path
          d={linePath}
          fill="none"
          stroke={t.success}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        />

        {/* Hover hit areas + tooltips */}
        {bins.map((bin, i) => {
          const x = padL + (i / Math.max(1, bins.length - 1)) * plotW;
          const hitW = plotW / bins.length;
          return (
            <rect
              key={i}
              x={x - hitW / 2}
              y={0}
              width={hitW}
              height={chartH}
              fill="transparent"
            >
              <title>
                {fmtWeek(bin.start)}: {bin.count} signoff{bin.count !== 1 ? 's' : ''}
                {bin.failed > 0 ? ` (${bin.passed} pass, ${bin.failed} fail)` : ''}
              </title>
            </rect>
          );
        })}

        {/* Data point dots for non-zero weeks */}
        {points.map(([x, y], i) =>
          bins[i].count > 0 ? (
            <circle key={i} cx={x} cy={y} r="2" fill={t.success} opacity={0.8} />
          ) : null
        )}

        {/* Month labels */}
        {monthLabels.map((ml, i) => (
          <text
            key={i}
            x={ml.x}
            y={totalH - 1}
            fill={t.textMuted}
            fontSize="8"
            fontFamily="system-ui, sans-serif"
          >
            {ml.label}
          </text>
        ))}
      </svg>
    </div>
  );
};
